import { observer } from 'mobx-react-lite'

import { useEffect } from 'react'
import {
  Alert,
  ChoiceTile,
  type IIconsVector,
  SpinnerLoader,
  Tooltip,
  TooltipLink,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { IntegrationsPath } from 'entities/ExportRouters'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import { ManualConversationAssignment } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/ui/ManualConversationAssignment/ManualConversationAssignment'
import { RoundRobinConversationAssignment } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/ui/RoundRobinConversationAssignment/RoundRobinConversationAssignment'
import { CRMConversationAssignment } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/conversationAssignment/ui/CRMConversationAssignment/CRMConversationAssignment'
import { type AssignmentType } from './store/conversationAssignmentStore'
import styles from './styles.module.scss'

export const ConversationAssignment = observer(() => {
  const {
    inboxSettingsStore: { conversationAssignmentStore },
    membersStore: { loadMembers },
    inboxId,
  } = useInboxLayerContext()

  const {
    conversationAssignment,
    loading,
    selectedAssignmentType,
    updateAssignmentType,
    assignmentOptions,
    isHubspotIntegrationConnected,
  } = conversationAssignmentStore

  useEffect(() => {
    return () => {
      loadMembers(inboxId, true)
    }
  }, [])

  if (loading && !conversationAssignment) {
    return <SpinnerLoader ariaLabel={'ConversationAssignment'} />
  }

  return (
    <div className={styles.wrap}>
      <Typography
        ariaLabel={'ConversationAssignmentTitle'}
        variant={'body-lg-medium'}
        marginBottom={'8px'}
      >
        Conversation assignment
      </Typography>
      {selectedAssignmentType === 'manual' && (
        <Alert
          item={{
            type: 'infoLight',
            desc: 'New conversations will be unassigned by default unless you pick a default assignee',
          }}
        />
      )}
      <div className={styles.cardsContainer}>
        {assignmentOptions.map((option) => {
          const isTooltipVisible =
            option.value === 'hubspot_assignment' && !isHubspotIntegrationConnected
          return (
            <Tooltip
              key={option.value}
              type='description'
              label={
                <Typography
                  variant={'body-md-regular'}
                  color={'var(--content-primary-primary-inverted)'}
                  ariaLabel={'HubspotIntegrationNotConnected'}
                >
                  Your integration isn’t connected
                </Typography>
              }
              placement={'top'}
              width={212}
              fullWidth
              withoutPointer
              visibility={isTooltipVisible ? 'visible' : 'hidden'}
              desc={
                <TooltipLink
                  text='Go to App Marketplace'
                  onClick={() => {
                    uiStore.changeRoute({
                      path: `${IntegrationsPath.root}`,
                    })
                  }}
                />
              }
            >
              <ChoiceTile
                className={styles.choiceTile}
                key={option.value}
                iconProps={{
                  icon: option.icon as IIconsVector,
                  fontSize: 32,
                  color: option.iconColor,
                }}
                title={option.title}
                description={option.description}
                isSimleIcon
                value={option.value}
                checked={selectedAssignmentType === option.value}
                setChoiceTile={(value) => updateAssignmentType(value as AssignmentType)}
                titleProps={{
                  ariaLabel: 'ChoiceTile',
                  variant: 'body-lg-medium',
                }}
                disabled={option.disabled}
              />
            </Tooltip>
          )
        })}
      </div>
      <div>
        {selectedAssignmentType === 'manual' && (
          <ManualConversationAssignment store={conversationAssignmentStore} />
        )}
        {selectedAssignmentType === 'round_robin' && (
          <RoundRobinConversationAssignment store={conversationAssignmentStore} />
        )}
        {selectedAssignmentType === 'hubspot_assignment' && (
          <CRMConversationAssignment store={conversationAssignmentStore} />
        )}
      </div>
    </div>
  )
})
